<section class="page-shell">
  <header class="page-header">
    <div class="left">
      <button mat-button routerLink="/"><mat-icon>arrow_back</mat-icon> Dashboard</button>
      <div class="title-block">
        <p class="eyebrow">Receipts</p>
        <h1>Keep paperwork crisp and organised</h1>
        <p class="subtitle">Review scanned receipts, auto-extract with ChatGPT, and line them up with transactions.</p>
      </div>
    </div>
    <div class="right">
      <button
        *ngIf="activeTab === 'unsorted' || activeTab === 'sorted'"
        type="button"
        mat-stroked-button
        color="primary"
        [disabled]="isBulkRunningChatGPT || !hasReceiptsAvailableForChatGPT"
        (click)="bulkRunChatGPT()">
        <mat-icon>auto_awesome</mat-icon>
        {{ isBulkRunningChatGPT ? 'ChatGPT bulk ' + bulkChatGPTCompleted + '/' + bulkChatGPTTotal : 'ChatGPT bulk (' + chatGPTBulkAvailableCount + ')' }}
      </button>
      <button color="primary" mat-raised-button (click)="openUploadDialog()"><mat-icon>cloud_upload</mat-icon> Upload</button>
    </div>
  </header>

  <div class="controls">
    <div class="status-cards">
      <button type="button" class="status-card warning" [class.active]="activeTab=='unsorted'" (click)="getUnsortedReceipts()">
        <p class="label">Unsorted</p>
        <div class="value">{{ unsortedCount }}</div>
        <p class="hint">Needs matching</p>
      </button>
      <button type="button" class="status-card success" [class.active]="activeTab=='sorted'" (click)="getSortedReceipts()">
        <p class="label">Sorted</p>
        <div class="value">{{ sortedCount }}</div>
        <p class="hint">Already matched</p>
      </button>
      <button type="button" class="status-card danger" [class.active]="activeTab=='deleted'" (click)="getDeletedReceipts()">
        <p class="label">Deleted</p>
        <div class="value">{{ deletedCount }}</div>
        <p class="hint">Hidden from queue</p>
      </button>
    </div>
    <div class="meta">
      <div class="pill soft">Updated live</div>
      <div class="pill ghost">Click a filename to preview</div>
    </div>
  </div>

  <div *ngIf="activeTab === 'sorted'" class="sorted-filters">
    <div class="filter-heading">
      <mat-icon>filter_list</mat-icon>
      <span>Filters</span>
    </div>
    <mat-checkbox [(ngModel)]="showAmountMismatchesOnly" (change)="getSortedReceipts()">
      ChatGPT amount differs from transaction
    </mat-checkbox>
    <span *ngIf="isLoadingSortedReceipts" class="filter-result-count">Loading receipts...</span>
    <span *ngIf="!isLoadingSortedReceipts && !showAmountMismatchesOnly" class="filter-result-count">Showing latest {{ receipts.length }} of {{ sortedCount }}</span>
    <span *ngIf="!isLoadingSortedReceipts && showAmountMismatchesOnly" class="filter-result-count">Showing {{ displayedReceipts.length }} mismatches</span>
  </div>

  <div class="receipts-list">
    <div
      class="receipt-card"
      *ngFor="let receipt of displayedReceipts; trackBy: trackByReceiptSha1"
      [class.has-receipt]="receiptHasTransaction(receipt)"
      [class.amount-mismatch]="hasAmountMismatch(receipt)">
      <div class="card-header">
        <div class="file-info">
          <div (click)="openReceipt(receipt)" class="name">{{ receipt.file?.filename || 'Missing file' }}</div>
          <div class="subtitle">
            {{ receipt.vender || 'Vendor not set' }}
          </div>
        </div>
        <div class="header-actions">
          <button
            type="button"
            mat-icon-button
            class="info-button"
            aria-label="Log receipt info"
            (click)="logReceipt(receipt)">
            <mat-icon>info_outline</mat-icon>
          </button>
          <div class="badges">
            <span *ngIf="receiptHasTransaction(receipt)" class="pill success">Matched to transaction</span>
            <span *ngIf="!receiptHasTransaction(receipt) && !receipt.missingFile" class="pill warning">Needs matching</span>
            <span *ngIf="receipt.missingFile" class="pill danger">Receipt file missing</span>
            <span *ngIf="hasAmountMismatch(receipt)" class="pill danger">
              {{ hasMultipleLinkedReceipts(receipt) ? 'Combined amount mismatch' : 'Amount mismatch' }}
            </span>
            <span *ngIf="isWaitingOnChatGPT(receipt)" class="pill neutral">ChatGPT is extracting</span>
            <span *ngIf="hasReceiptBeenProcessedByChatGPT(receipt) && !isWaitingOnChatGPT(receipt)" class="pill success">ChatGPT complete</span>
            <span *ngIf="receipt.note" class="pill ghost">Has note</span>
          </div>
        </div>
      </div>

      <div class="details">
        <label class="detail">
          <span class="label">Date</span>
          <ap-date [(date)]="receipt.date" (dateChange)="updateReceipt(receipt)"></ap-date>
        </label>

        <label class="detail">
          <span class="label">Receipt amount</span>
          <ap-money mat-input class="payment-amount-input" matInput [(amount)]="receipt.amount" [inDollars]="true" (amountChange)="updateReceipt(receipt)"></ap-money>
          <span
            *ngIf="hasMatchedTransactionAmount(receipt)"
            class="matched-transaction-amount"
            [class.mismatch]="hasAmountMismatch(receipt)">
            Transaction: {{ formatMatchedTransactionAmount(receipt) }}
          </span>
          <span
            *ngIf="hasMultipleLinkedReceipts(receipt)"
            class="linked-receipt-total"
            [class.mismatch]="hasAmountMismatch(receipt)"
            [class.incomplete]="receipt.linkedReceiptAmountsComplete !== true">
            {{ receipt.linkedReceiptCount }} linked receipts: {{ formatLinkedReceiptTotal(receipt) }}
          </span>
        </label>

        <div class="detail actions">
          <div class="action-buttons">
            <button
              *ngIf="!isWaitingOnChatGPT(receipt)"
              mat-button
              [disabled]="receipt.missingFile || isBulkRunningChatGPT || chatGPTRequestShas.size >= 5"
              (click)="askChatGPT(receipt)"><mat-icon>auto_awesome</mat-icon> {{ hasReceiptBeenProcessedByChatGPT(receipt) ? 'Run ChatGPT again' : 'Ask ChatGPT' }}</button>
            <button *ngIf="isWaitingOnChatGPT(receipt)" mat-button class="waiting">
              <svg class="chatgpt-spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                <path fill="none" stroke="#00A67E" stroke-width="2"
                  d="M16 2a14 14 0 1 0 14 14A14 14 0 0 0 16 2z M16 2v28M2 16h28" />
              </svg>
              Waiting on ChatGPT
            </button>

            <button class="find-transaction" mat-stroked-button (click)="findTransaction(receipt)" [disabled]="!receipt.date"><mat-icon>travel_explore</mat-icon> Find Transaction</button>
            <button class="show-transaction" mat-stroked-button (click)="showTransaction(receipt)"><mat-icon>receipt_long</mat-icon> Show Transaction</button>
            <button mat-stroked-button color="warn" (dblclick)="deleteReceipt(receipt)"><mat-icon>delete</mat-icon> Delete</button>
          </div>
        </div>

        <label class="detail note">
          <span class="label">Notes</span>
          <textarea
            class="note-textarea"
            rows="2"
            placeholder="Leave a note if you can't reconcile it yet"
            [(ngModel)]="receipt.note"
            (blur)="saveReceiptNote(receipt)"></textarea>
          <span class="note-hint">Saved automatically when you click away.</span>
        </label>

      </div>
    </div>

    <div *ngIf="activeTab === 'sorted' && showAmountMismatchesOnly && !isLoadingSortedReceipts && displayedReceipts.length === 0" class="empty-filter-state">
      <mat-icon>check_circle</mat-icon>
      <span>No amount mismatches found.</span>
    </div>
  </div>
</section>
